import { useEffect, useMemo } from 'react';
import { Formik } from 'formik';
import { gql } from '@apollo/client';
import { useParams } from 'react-router';
import * as yup from 'yup';
import { useDataScope, useRecord } from '../../utils/hooks';
import { useSetBreadcrumbEffect } from '../../contexts/BreadcrumbContext';
import {
  DeleteButton,
  Message,
  SubmitButton,
  TemplateForm,
  ValidatedInput,
} from './template-components';
import {
  CreateInputMutation,
  CreateInputResourceMutation,
  DeleteInputMutation,
  DeleteInputResourceMutation,
  UpdateInputMutation,
  UpdateInputResourceMutation,
} from '../../mutations';
import { InputResourcesQuery, RawInputsQuery } from './InputsTable';
import {
  INPUT_TYPE_MATERIALS,
  useInputTypeContext,
} from '../../contexts/InputTypeContext';
import {
  alphanumericSort,
  getFarmPath,
  objectWithFields,
} from '../../utils/utils';
import ExpandableItemsList from '../ExpandableItemsList';
import { asQuantityWithUnit } from '../../utils/formatters';
import { captureEvent } from '../../utils/analytics';
import UnderlinedLink from '../basics/UnderlinedLink';

const fields = [
  'id',
  'name',
  'unit',
  'packageCost',
  'shippingCostMultiplier',
  'packageQuantity',
  'updatedAt',
];

const queryString = fields.join(' ');

const referencedByOperationsString = `
  referencedByOperations {
    crop {
      id
      name
    }
    id
    name
  }
`;

const InputResourceQuery = gql`
  query InputResource($id: ID!) {
    inputResource(id: $id) {
      ${queryString}
      ${referencedByOperationsString}
    }
  }
`;

const RawInputQuery = gql`
  query RawInput($id: ID!) {
    rawInput(id: $id) {
      ${queryString}
      ${referencedByOperationsString}
    }
  }
`;

const defaultInput = {
  ...objectWithFields(fields),
  unit: 'unit',
  shippingCostMultiplier: 1,
  packageQuantity: 1,
};

const inputSchema = yup.object().shape({
  name: yup.string().required('Required'),
  unit: yup.string().nullable(),
  packageCost: yup.number().min(0).required('Required'),
  shippingCostMultiplier: yup
    .number()
    .min(1, ({ min }) => `Must be greater than ${min}`),
  packageQuantity: yup
    .number()
    .positive('Must be positive')
    .integer('Must be a whole number')
    .required('Required'),
});

const formatOperationLink = (scope) => (operation) => {
  const { crop, id, name } = operation;
  const path = `../../${scope === 'farm' ? 'crops' : 'templates'}/${
    crop.id
  }/operations/${id}`;
  return (
    <UnderlinedLink to={path}>
      {crop.name}: {name}
    </UnderlinedLink>
  );
};

const transformValues = (values) => ({
  ...values,
  referencedByOperations: undefined,
  __typename: undefined,
});

const EditInput = ({
  onSubmit,
  onDelete,
  status,
  msg,
  resource = defaultInput,
  basePath,
}) => {
  const { id, teamId } = useParams();
  const { loading } = status;
  const { setSelectedType } = useInputTypeContext();
  const scope = useDataScope();
  useEffect(() => {
    setSelectedType(INPUT_TYPE_MATERIALS);
  }, [setSelectedType]);

  const breadcrumbs = [
    { display: INPUT_TYPE_MATERIALS, url: basePath },
    { display: id ? resource.name || '...' : 'New' },
  ];
  useSetBreadcrumbEffect(breadcrumbs, [id, resource.name, teamId]);

  // Only add teamId if we are creating an input, cannot update the team after creation
  const initialValues = id ? resource : { teamId, ...resource };

  const referencedByOperations = useMemo(
    () =>
      [...(resource.referencedByOperations || [])].sort(
        (a, b) =>
          // Compare operation names if crop names match
          alphanumericSort(a.crop.name, b.crop.name) ||
          alphanumericSort(a.name, b.name),
      ),
    [resource.referencedByOperations],
  );

  if (msg) return <Message>{msg}</Message>;

  return (
    <Formik
      enableReinitialize
      initialValues={initialValues}
      validationSchema={inputSchema}
      onSubmit={onSubmit(inputSchema, 'Input saved successfully')}
    >
      {({ isSubmitting }) => (
        <TemplateForm>
          <ValidatedInput type="text" name="name" label="Name" />
          <ValidatedInput type="text" name="unit" label="Unit" />
          <div className="d-flex">
            <ValidatedInput
              type="number"
              name="packageCost"
              label="Package cost"
              min={0}
              step={0.01}
              currency
            />
            <ValidatedInput
              type="number"
              name="shippingCostMultiplier"
              label="Shipping multiplier"
              min={1}
              step="any"
            />
          </div>
          <ValidatedInput
            type="number"
            name="packageQuantity"
            label="Package quantity"
            min={1}
            step={1}
          />
          {id && (
            <>
              <div className="mb-3">
                <ExpandableItemsList
                  items={referencedByOperations}
                  title={`In use on ${asQuantityWithUnit(
                    'operations',
                    'operation',
                  )(referencedByOperations?.length)}`}
                  formatItem={formatOperationLink(scope)}
                  onToggle={(newToggleState) => {
                    // Record an event when someone opens the list
                    if (newToggleState) {
                      captureEvent('input-reference-list-expanded', {
                        scope,
                      });
                    }
                  }}
                />
              </div>
              <DeleteButton
                onDelete={onDelete}
                disabled={isSubmitting || loading}
                className="mr-2"
              />
            </>
          )}
          <SubmitButton loading={loading} />
        </TemplateForm>
      )}
    </Formik>
  );
};

export const EditFarmInput = () => {
  const { id } = useParams();
  const { farmId } = useParams();
  const basePath = getFarmPath(farmId, 'data/inputs');
  const props = useRecord({
    submitMutation: id ? UpdateInputMutation : CreateInputMutation,
    deleteMutation: DeleteInputMutation,
    fetchRecordQuery: RawInputQuery,
    onDeleteRefetchQuery: RawInputsQuery,
    creationRedirectUrl: `${basePath}/:id/edit`,
    deletionRedirectUrl: basePath,
    includeFarmId: !id,
    transformValues,
  });

  return <EditInput {...props} basePath={basePath} />;
};

export const EditInputResource = () => {
  const { id, teamId } = useParams();
  const basePath = teamId ? ':rootUrl/inputs' : '/superadmin/inputs';
  const props = useRecord({
    submitMutation: id
      ? UpdateInputResourceMutation
      : CreateInputResourceMutation,
    deleteMutation: DeleteInputResourceMutation,
    fetchRecordQuery: InputResourceQuery,
    onDeleteRefetchQuery: InputResourcesQuery,
    creationRedirectUrl: '../:id/edit',
    deletionRedirectUrl: '../..',
    transformValues,
  });
  return <EditInput {...props} basePath={basePath} />;
};
